import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { GAME_CONFIG } from './constants';
import { getRandomQuestionSet } from './utils/questionUtils';
import HomeScreen from './components/HomeScreen/HomeScreen';
import QuestionScreen from './components/QuestionScreen/QuestionScreen';
import GameEndScreen from './components/GameEndScreen/GameEndScreen';
import MusicConsentModal from './components/MusicConsentModal/MusicConsentModal';

function App() {
  const [gameState, setGameState] = useState('home');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [timeLeft, setTimeLeft] = useState(GAME_CONFIG.QUESTION_TIMER_SECONDS);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [gameResult, setGameResult] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [timerPaused, setTimerPaused] = useState(false);
  const [answersDisabled, setAnswersDisabled] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionSet, setCurrentQuestionSet] = useState(null);
  const [musicStarted, setMusicStarted] = useState(false);
  const [showMusicModal, setShowMusicModal] = useState(true);
  const audioRef = useRef(null);

  // Load random question set on component mount
  useEffect(() => {
    try {
      const randomSet = getRandomQuestionSet();
      setQuestions(randomSet.questions);
      setCurrentQuestionSet(randomSet.setInfo);
    } catch (error) {
      console.error('Error loading question set:', error);
      // Fallback to empty array if there's an error
      setQuestions([]);
    }
  }, []);

  // Background music effect
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = 0.3; // Set volume to 30%
      audio.loop = true;
      
      // Don't auto-play, wait for user interaction through modal
      // Background music will be started when user clicks the consent modal
    }
  }, []);



  // Timer effect
  useEffect(() => {
    let timer;
    
    if (gameState === 'question' && timeLeft > 0 && !showResult && !timerPaused) {
      timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    } else if (gameState === 'question' && timeLeft === 0 && !showResult && !timerPaused) {
      // Time's up - show result overlay and pause timer
      console.log('Timer expired - showing timesUp result and pausing timer');
      setGameResult('timesUp');
      setShowResult(true);
      setTimerPaused(true);
      setAnswersDisabled(true); // Disable answers on timeout
    }
    
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [gameState, timeLeft, showResult, timerPaused]);

  const startGame = () => {
    // Start background music on first user interaction (if not already started)
    if (!musicStarted) {
      handleUserInteraction();
    }
    
    // Use the already loaded question set instead of selecting a new one
    if (!questions || questions.length === 0) {
      console.error('No questions available to start game');
      return;
    }
    
    // Reset game state without changing questions
    setCurrentQuestion(0);
    setScore(0);
    setSelectedAnswer(null);
    setTimeLeft(GAME_CONFIG.QUESTION_TIMER_SECONDS);
    setGameState('question');
    setShowResult(false);
    setTimerPaused(false);
    setAnswersDisabled(false);
  };

  const handleAnswerSelect = (answerIndex) => {
    if (answersDisabled) return; // Don't allow selection if answers are disabled
    
    console.log('Answer selected, pausing timer and disabling answers');
    setTimerPaused(true); // Pause the timer immediately
    setAnswersDisabled(true); // Disable further answer selection
    setSelectedAnswer(answerIndex);
    const correct = questions[currentQuestion].correct;
    if (answerIndex === correct) {
      setScore(score + 1);
      setGameResult('correct');
    } else {
      setGameResult('incorrect');
    }
    setShowResult(true);
  };

  const nextQuestion = () => {
    setSelectedAnswer(null);
    setShowResult(false);
    setTimerPaused(false); // Resume timer for next question
    setAnswersDisabled(false); // Re-enable answers for next question
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setTimeLeft(GAME_CONFIG.QUESTION_TIMER_SECONDS);
    } else {
      // Game completed - select a new question set for next game
      try {
        const randomSet = getRandomQuestionSet();
        setQuestions(randomSet.questions);
        setCurrentQuestionSet(randomSet.setInfo);
      } catch (error) {
        console.error('Error loading next question set:', error);
        // Keep current questions if there's an error
      }
      setGameState('gameEnd');
    }
  };

  const hideResultOverlay = () => {
    setShowResult(false);
    // Timer stays paused and answers stay disabled until next question
  };

  const returnHome = () => {
    // Reset game state first
    setCurrentQuestion(0);
    setScore(0);
    setSelectedAnswer(null);
    setTimeLeft(GAME_CONFIG.QUESTION_TIMER_SECONDS);
    setShowResult(false);
    setTimerPaused(false);
    setAnswersDisabled(false);
    
    // Select a new random question set when returning home
    try {
      const randomSet = getRandomQuestionSet();
      setQuestions(randomSet.questions);
      setCurrentQuestionSet(randomSet.setInfo);
    } catch (error) {
      console.error('Error loading question set:', error);
      // Keep current questions if there's an error
    }
    
    setGameState('home');
  };

  const startBackgroundMusic = async () => {
    const audio = audioRef.current;
    if (audio && !musicStarted) {
      try {
        await audio.play();
        setMusicStarted(true);
      } catch (error) {
        console.log('Failed to start background music:', error);
      }
    }
  };

  const handleUserInteraction = () => {
    startBackgroundMusic();
  };

  const handleMusicConsent = async () => {
    setShowMusicModal(false);
    await startBackgroundMusic();
  };

  const renderScreen = () => {
    switch (gameState) {
      case 'question':
        // Don't render question screen if no questions are loaded, fallback to home
        if (!questions || questions.length === 0) {
          return <HomeScreen startGame={startGame} questionCount={0} />;
        }
        return (
          <QuestionScreen
            questions={questions}
            currentQuestion={currentQuestion}
            timeLeft={timeLeft}
            selectedAnswer={selectedAnswer}
            handleAnswerSelect={handleAnswerSelect}
            onGoHome={returnHome}
            gameResult={gameResult}
            showResult={showResult}
            nextQuestion={nextQuestion}
            hideResultOverlay={hideResultOverlay}
            answersDisabled={answersDisabled}
          />
        );
      case 'gameEnd':
        return (
          <GameEndScreen
            score={score}
            questions={questions}
            returnHome={returnHome}
          />
        );
      case 'home':
      default:
        return <HomeScreen startGame={startGame} questionCount={questions.length} />;
    }
  };

  return (
    <div className="App">
      <div 
        className="background-image"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: 'url(/images/MC_bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          zIndex: -1
        }}
      />
      
      {/* Background Music */}
      <audio 
        ref={audioRef}
        src="/background.mp3"
        preload="auto"
      />
      
      {/* Music Consent Modal */}
      {showMusicModal && (
        <MusicConsentModal onStartMusic={handleMusicConsent} />
      )}
      
      {renderScreen()}
    </div>
  );
}

export default App; 